import {Dispatch, SetStateAction, useEffect, useRef, useState} from 'react';

import classNames from "classnames";

import styles from "./SearchPage.module.scss";
import FontAwesome from "../../components/utilities/FontAwesome";
import {ExecutionEnvironment} from "../../stores/InterfaceStore";
import {useStores} from "../../stores/useStores";

const SearchBox: React.FC<{
    searchTerm: string;
    isValidating: boolean;
    inputValue: string;
    setInputValue: Dispatch<SetStateAction<string>>;
    handleSubmit: () => void;
    placeholder?: string;
}> = (props) => {
    const {organizationStore, gridStore} = useStores();
    const searchRef = useRef(null);
    const submitRef = useRef<HTMLButtonElement | null>(null);

    const [listening, setListening] = useState(false);
    const [recognitionInstance, setRecognitionInstance] = useState<any>(null);
    const [isClient, setIsClient] = useState(false);

    useEffect(() => {
        setIsClient(true);

        if (
            ExecutionEnvironment.canUseDOM &&
            // @ts-ignore
            (window.SpeechRecognition || window.webkitSpeechRecognition)
        ) {
            // @ts-ignore
            const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
            if (SpeechRecognitionAPI && typeof SpeechRecognitionAPI !== "undefined") {
                const r = new SpeechRecognitionAPI();
                r.interimResults = true;
                r.maxAlternatives = 1;
                setRecognitionInstance(r);
            }
        }
    }, []);

    function handleClick(e: React.MouseEvent<HTMLButtonElement>) {
        e.preventDefault();
        if (listening && recognitionInstance) {
            recognitionInstance.abort();
            setListening(false);
        } else if (recognitionInstance) {
            recognitionInstance.start();
            setListening(true);
        }
    }

    function handleMouseDown(e: React.MouseEvent<HTMLButtonElement>) {
        e.preventDefault();
    }

    function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
        props.setInputValue(e.target.value);
    }

    useEffect(() => {
        if (recognitionInstance) {
            const handleResult = (event: any) => {
                const text = event.results[0][0].transcript;
                props.setInputValue(text);

                if (event.results[0].isFinal) {
                    setListening(false);
                    submitRef.current?.click();
                }
            };

            const handleError = (event: any) => {
                setListening(false);
                let message: string | null = null;
                switch (event.error) {
                    case "network":
                        message =
                            "There was a problem connecting to the network, please check your connection or browser settings to enable speech to text.";
                        break;
                    case "aborted":
                        // No message for user-initiated abort
                        break;
                    default:
                        message =
                            "An error occurred! Please enable microphone access and try again!";
                        break;
                }
                if (message) {
                    alert(message);
                }
            };

            recognitionInstance.onresult = handleResult;
            recognitionInstance.onerror = handleError;

            return () => {
                if (recognitionInstance) {
                    recognitionInstance.onresult = null;
                    recognitionInstance.onerror = null;
                }
            };
        }
    }, [recognitionInstance, props.setInputValue]); // Add props.handleSubmit if directly called here

    const searchClassName = classNames({
        [styles.search]: true,
    });

    return (
        <div className={styles.searchForm}>
            <input
                type="hidden"
                name="orgId"
                value={organizationStore.organization.id}
            />
            <div className={searchClassName}>
                <input
                    ref={searchRef}
                    id="sb-search-input-large"
                    name="q"
                    type="text"
                    aria-label="Search this site"
                    placeholder={props.placeholder || "Search"}
                    value={props.inputValue}
                    onChange={handleChange}
                    autoComplete="off"
                    title="Search"
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            props.handleSubmit();
                        }
                    }}
                />
                {props.isValidating && (
                    <FontAwesome prefix={"fas"} name={"fa-spinner"} spin />
                )}
                <button
                    type="submit"
                    ref={submitRef}
                    className={styles.eyeglass}
                    title="Submit search"
                    aria-label="Submit search"
                    onClick={props.handleSubmit}
                >
                    <FontAwesome
                        name={"fa-search"}
                        prefix={"fas"}
                        size={"lg"}
                        className={styles.darkIcon}
                        aria-hidden={true}
                    />
                </button>
                {isClient && recognitionInstance && (
                    <button
                        title={"Search by voice"}
                        aria-label={"Search by voice"}
                        onClick={handleClick}
                        onMouseDown={handleMouseDown}
                        className={styles.speech}
                    >
                        <FontAwesome
                            name={`${listening ? "fa-volume-down" : "fa-microphone"}`}
                            prefix={"fas"}
                            size={"lg"}
                            className={styles.darkIcon}
                            aria-hidden={true}
                        />
                    </button>
                )}
            </div>
        </div>
    );
};
export default SearchBox;
